import React, { useState, useMemo, useRef, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { cn } from "@/helper/utils";

interface DatePickerFieldProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  min?: string;
  max?: string;
  rangeEnd?: string;
  showWeekNumbers?: boolean;
  rightPanel?: React.ReactNode;
  className?: string;
  compact?: boolean;
}

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}/${year}`;
}

function toDateStr(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function parseDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function getISOWeekNumber(year: number, month: number, day: number): number {
  const date = new Date(year, month, day);
  const jan4 = new Date(date.getFullYear(), 0, 4);
  const dayOfYear = Math.floor(
    (date.getTime() - new Date(date.getFullYear(), 0, 1).getTime()) / 86400000
  ) + 1;
  const jan4DayOfWeek = jan4.getDay() || 7;
  const weekNumber = Math.ceil((dayOfYear + jan4DayOfWeek - 1) / 7);
  return weekNumber;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  min,
  max,
  rangeEnd,
  showWeekNumbers = false,
  rightPanel,
  className,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const initialDate = value ? parseDate(value) : new Date();
  const [viewYear, setViewYear] = useState(initialDate?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate?.getMonth() ?? new Date().getMonth());

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setSelectedDate(value);
      const d = value ? parseDate(value) : new Date();
      if (d) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
    setOpen(isOpen);
  };

  const animateMonth = (direction: "left" | "right", callback: () => void) => {
    setSlideDirection(direction);
    setIsAnimating(true);
    setTimeout(() => {
      callback();
      setSlideDirection(null);
      setIsAnimating(false);
    }, 200);
  };

  const prevMonth = () => {
    animateMonth("right", () => {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    });
  };

  const nextMonth = () => {
    animateMonth("left", () => {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear(viewYear + 1);
      } else {
        setViewMonth(viewMonth + 1);
      }
    });
  };

  const goToToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  };

  const handleClear = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onChange("");
    setSelectedDate("");
    setOpen(false);
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const days: { day: number; month: number; year: number; isCurrentMonth: boolean }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
      const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
      days.push({ day: daysInPrevMonth - i, month: prevM, year: prevY, isCurrentMonth: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, month: viewMonth, year: viewYear, isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const nextM = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
      days.push({ day: d, month: nextM, year: nextY, isCurrentMonth: false });
    }

    return days;
  }, [viewYear, viewMonth]);

  const weekNumbers = useMemo(() => {
    if (!showWeekNumbers) return [];
    const weeks: number[] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      const cell = calendarDays[i];
      weeks.push(getISOWeekNumber(cell.year, cell.month, cell.day));
    }
    return weeks;
  }, [calendarDays, showWeekNumbers]);

  const isDisabled = (year: number, month: number, day: number): boolean => {
    const dateStr = toDateStr(year, month, day);
    if (min && dateStr < min) return true;
    if (max && dateStr > max) return true;
    return false;
  };

  const isInRange = (dateStr: string): boolean => {
    if (!rangeEnd || !selectedDate) return false;
    const start = selectedDate < rangeEnd ? selectedDate : rangeEnd;
    const end = selectedDate < rangeEnd ? rangeEnd : selectedDate;
    return dateStr > start && dateStr < end;
  };

  const isRangeEdge = (dateStr: string): boolean => {
    if (!rangeEnd || !selectedDate) return false;
    return dateStr === selectedDate || dateStr === rangeEnd;
  };

  const todayStr = useMemo(() => {
    const now = new Date();
    return toDateStr(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const handleApply = () => {
    if (selectedDate) {
      onChange(selectedDate);
    }
    setOpen(false);
  };

  const gridCols = showWeekNumbers ? "grid-cols-8" : "grid-cols-7";

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center border border-[#C72BA4] hover:border-[#EA33C0] rounded-lg focus:outline-none transition duration-200 text-left group",
            compact ? "gap-1.5 px-2 py-2.5 text-xs" : "gap-2 px-3 py-2.5 text-sm",
            className
          )}
        >
          <Calendar size={compact ? 12 : 16} className="text-gray-400 shrink-0" />
          <span className={cn("flex-1 whitespace-nowrap", value ? "text-gray-900" : "text-gray-400")}>
            {value ? formatDisplay(value) : placeholder}
          </span>
          {value && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            >
              <X size={compact ? 10 : 14} />
            </span>
          )}
        </button>
      </DialogTrigger>

      <DialogContent
        className={cn(
          "sm:max-w-2xl p-0 overflow-hidden bg-white",
          !rightPanel && "sm:max-w-md"
        )}
        showCloseButton
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Select Date</DialogTitle>
        </DialogHeader>

        <div className="flex">
          {/* Left panel — Calendar */}
          <div className={cn("flex-1 px-6 pb-4", rightPanel && "border-r border-gray-200")}>
            {/* Month/Year navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={prevMonth}
                disabled={isAnimating}
                className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex items-center gap-1">
                <select
                  value={viewMonth}
                  onChange={(e) => setViewMonth(Number(e.target.value))}
                  className="text-sm font-semibold text-gray-900 bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4 bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0_center]"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={idx} value={idx}>{name}</option>
                  ))}
                </select>
                <select
                  value={viewYear}
                  onChange={(e) => setViewYear(Number(e.target.value))}
                  className="text-sm font-semibold text-gray-900 bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4 bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0_center]"
                >
                  {Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={nextMonth}
                disabled={isAnimating}
                className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Day-of-week headers */}
            <div className={cn("grid mb-1", gridCols)}>
              {showWeekNumbers && (
                <div className="text-center text-xs font-medium text-gray-400 py-1">Wk</div>
              )}
              {DAYS_OF_WEEK.map((d) => (
                <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Day grid with animation */}
            <div className="overflow-hidden">
              <div
                ref={gridRef}
                className={cn(
                  "grid transition-all duration-200 ease-in-out",
                  gridCols,
                  slideDirection === "left" && "translate-x-[-8px] opacity-0",
                  slideDirection === "right" && "translate-x-[8px] opacity-0",
                  !slideDirection && "translate-x-0 opacity-100"
                )}
              >
                {calendarDays.map((cell, idx) => {
                  const dateStr = toDateStr(cell.year, cell.month, cell.day);
                  const isSelected = selectedDate === dateStr;
                  const isToday = dateStr === todayStr;
                  const disabled = isDisabled(cell.year, cell.month, cell.day);
                  const inRange = isInRange(dateStr) && cell.isCurrentMonth;
                  const rangeEdge = isRangeEdge(dateStr) && cell.isCurrentMonth;

                  const isWeekStart = idx % 7 === 0;

                  return (
                    <React.Fragment key={idx}>
                      {showWeekNumbers && isWeekStart && (
                        <div className="h-9 flex items-center justify-center text-xs text-gray-400 font-medium">
                          {weekNumbers[idx / 7]}
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={disabled || !cell.isCurrentMonth}
                        onClick={() => !disabled && cell.isCurrentMonth && setSelectedDate(dateStr)}
                        className={cn(
                          "h-9 w-full text-sm transition-colors relative",
                          !inRange && !rangeEdge && "rounded-md",
                          !cell.isCurrentMonth && "text-gray-300 cursor-default",
                          cell.isCurrentMonth && !disabled && !isSelected && !inRange && "text-gray-700 hover:bg-pink-50",
                          cell.isCurrentMonth && disabled && "text-gray-300 cursor-not-allowed",
                          isSelected && "bg-[#EA33C0] text-white hover:bg-[#C72BA4] rounded-md z-10 relative",
                          inRange && "bg-pink-50 text-gray-700",
                          rangeEdge && !isSelected && "bg-pink-100 text-[#EA33C0] font-semibold rounded-md",
                          isToday && !isSelected && cell.isCurrentMonth && "font-bold"
                        )}
                      >
                        {cell.day}
                        {isToday && !isSelected && cell.isCurrentMonth && (
                          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#EA33C0]" />
                        )}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Today button */}
            <div className="flex justify-center mt-3">
              <button
                type="button"
                onClick={goToToday}
                className="text-xs text-[#EA33C0] hover:text-[#C72BA4] font-medium transition-colors"
              >
                Today
              </button>
            </div>
          </div>

          {/* Right panel — future toggles/conditions slot */}
          {rightPanel && (
            <div className="flex-1 px-6 pb-4 pt-2">
              {rightPanel}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 pb-5 pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              setSelectedDate("");
            }}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selectedDate}
            className={cn(
              "px-6 py-2 rounded-full text-sm font-semibold text-white transition-colors",
              selectedDate
                ? "bg-[#EA33C0] hover:bg-[#C72BA4]"
                : "bg-gray-300 cursor-not-allowed"
            )}
          >
            Apply
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DatePickerField;
